import React, { useCallback, useEffect, useState } from 'react';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { salesFlow } from '../../help/flows';
import { SALES_INVOICE_STAGES } from '../../help/statusStages';
import FcAmountLine from '../common/FcAmountLine';
import { isForeignCurrency, formatFc } from '../../utils/fcDocuments';
import { invoiceBalanceDue, invoiceStatusLabel } from './documentLabels';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Link, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { ReceiptService } from '../../services/receipt.service';
import { CreditNoteService } from '../../services/creditNote.service';
import { CustomerService } from '../../services/customer.service';
import { PaymentLinkService } from '../../services/paymentLink.service';
import { Availability, PaymentLink } from '../../interfaces/sales/paymentLink.interface';
import { CreditNote } from '../../interfaces/sales/creditNote.interface';
import {
  SalesInvoice, SalesInvoiceStatus, SALES_INVOICE_STATUS_COLORS,
} from '../../interfaces/sales/salesInvoice.interface';
import { Receipt } from '../../interfaces/sales/receipt.interface';
import RecordReceiptDialog from './RecordReceiptDialog';
import { CashSaleService } from '../../services/cashSale.service';   // FW-2B
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import AttachmentsPanel from '../common/AttachmentsPanel';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, formatUnitPrice, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { hasDiscount } from '../../utils/salesLineDiscount';   // FW-2A
import { taxCodeLabel, useTaxCodeLookup } from '../tax/useTaxCodeLookup';
import { DocumentTaxCell, useDocumentTaxBreakdown } from '../tax/DocumentTaxBreakdown';
import { MushakService } from '../../services/mushak.service';
import { saveBlob } from '../../utils/saveBlob';
import ApprovalStatusCard from '../approvals/ApprovalStatusCard';   // FW-3A
import { projectTagLabel } from '../project/ProjectSelect';   // P1-A tail

// ReverseConfirmDialog props: open, entityLabel, consequence, onCancel, onConfirm, loading?
// It does NOT have a reason input — we collect voidReason via our own confirm dialog.

const STATUS_COLORS = SALES_INVOICE_STATUS_COLORS;

const SalesInvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.SALES_INVOICE_MANAGE);
  const canManageReceipts = usePermission(PERMISSIONS.RECEIPT_MANAGE);

  const [invoice, setInvoice] = useState<SalesInvoice | null>(null);
  const anyCodedLine = !!invoice?.lines?.some(l => l.taxCodeId);
  const anyDisc = hasDiscount(invoice?.lines ?? []);   // FW-2A
  const taxLookup = useTaxCodeLookup(anyCodedLine ? invoice?.institutionId : null);
  const taxComponents = useDocumentTaxBreakdown('sales-invoice', invoice?.id, anyCodedLine);
  // Mushak 6.3 is offered for Bangladesh tax profiles (uncoded lines print their free-typed rate as VAT).
  const [bangladesh, setBangladesh] = useState(false);
  const [mushakBusy, setMushakBusy] = useState(false);
  const invoiceInstitutionId = invoice?.institutionId;
  useEffect(() => {
    if (!invoiceInstitutionId) { setBangladesh(false); return undefined; }
    let cancelled = false;
    Promise.resolve(MushakService.isBangladesh(invoiceInstitutionId))
      .then(v => { if (!cancelled) setBangladesh(!!v); })
      .catch(() => { if (!cancelled) setBangladesh(false); });
    return () => { cancelled = true; };
  }, [invoiceInstitutionId]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  // W3-E: credit notes raised against this invoice (best effort: a failure just hides the section).
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState(true);

  // Issue dialog
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issuing, setIssuing] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Void dialog state (custom because ReverseConfirmDialog has no reason field)
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  // Record receipt dialog
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);

  // Email in-flight
  const [emailing, setEmailing] = useState(false);
  // W3-T payment link
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkAvail, setLinkAvail] = useState<Availability | null>(null);
  const [linkEmail, setLinkEmail] = useState<string | undefined>(undefined);
  const [existingLinks, setExistingLinks] = useState<PaymentLink[]>([]);
  const [createdLink, setCreatedLink] = useState<PaymentLink | null>(null);

  // PDF download in-flight
  const [downloading, setDownloading] = useState(false);

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const [inv, recs] = await Promise.all([
        SalesInvoiceService.getById(id),
        ReceiptService.listByInvoice(id),
      ]);
      setInvoice(inv);
      setReceipts(recs);
      Promise.resolve(CreditNoteService.listByInvoice(id))
        .then(n => setCreditNotes(Array.isArray(n) ? n : []))
        .catch(() => setCreditNotes([]));
    } catch (e) {
      toast.showApiError(e, 'Failed to load invoice');
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIssue = async () => {
    if (!invoice) return;
    setIssuing(true);
    try {
      const updated = await SalesInvoiceService.issue(invoice.id);
      setInvoice(updated);
      // One message only: the credit-control warning(s) ride on the "issued" toast, never a second toast or inline copy.
      const warnings = Array.from(new Set(updated.warnings ?? []));
      if (warnings.length > 0) {
        const message = `Invoice issued. ${warnings.join(' ')}`;
        if (toast.showWarning) toast.showWarning(message); else toast.showSuccess(message);
      } else {
        toast.showSuccess('Invoice issued');
      }
      setIssueDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to issue invoice');
    } finally {
      setIssuing(false);
    }
  };

  // ---- FW-3A: submit / withdraw the approval request of a draft ----
  const [approvalBusy, setApprovalBusy] = useState(false);
  const handleApproval = async (submit: boolean) => {
    if (!invoice) return;
    setApprovalBusy(true);
    try {
      const updated = submit ? await SalesInvoiceService.submitForApproval(invoice.id) : await SalesInvoiceService.withdrawApproval(invoice.id);
      setInvoice(updated);
      toast.showSuccess(submit ? 'Sent for approval' : 'Approval request withdrawn');
    } catch (e) {
      toast.showApiError(e, submit ? 'Failed to submit for approval' : 'Failed to withdraw the request');
    } finally {
      setApprovalBusy(false);
    }
  };
  // ---- end FW-3A ----

  const handleDelete = async () => {
    if (!invoice) return;
    setDeleting(true);
    try {
      await SalesInvoiceService.remove(invoice.id);
      toast.showSuccess('Invoice deleted');
      navigate('/sales/invoices');
    } catch (e) {
      toast.showApiError(e, 'Failed to delete invoice');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleVoid = async () => {
    if (!invoice || !voidReason.trim()) return;
    setVoiding(true);
    try {
      if (invoice.saleKind === 'CASH') {   // FW-2B: reverse every receipt and void the invoice in one step
        await CashSaleService.voidSale(invoice.id, voidReason.trim());
        await reload();
        toast.showSuccess('Cash sale voided');
        setVoidDialogOpen(false);
        setVoidReason('');
        return;
      }
      const updated = await SalesInvoiceService.voidInvoice(invoice.id, { voidReason: voidReason.trim() });
      setInvoice(updated);
      toast.showSuccess('Invoice voided');
      setVoidDialogOpen(false);
      setVoidReason('');
    } catch (e) {
      toast.showApiError(e, 'Failed to void invoice');
    } finally {
      setVoiding(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoice) return;
    setDownloading(true);
    try {
      const blob = await SalesInvoiceService.downloadPdf(invoice.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.invoiceNumber ?? invoice.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.showApiError(e, 'Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadMushak63 = async () => {
    if (!invoice) return;
    setMushakBusy(true);
    try {
      const blob = await MushakService.download63Pdf(invoice.id);
      saveBlob(blob, `mushak-6-3-${invoice.invoiceNumber ?? invoice.id}.pdf`);
    } catch (e) {
      toast.showApiError(e, 'Failed to download Mushak 6.3');
    } finally {
      setMushakBusy(false);
    }
  };

  const handleEmail = async () => {
    if (!invoice) return;
    setEmailing(true);
    try {
      await SalesInvoiceService.email(invoice.id);
      toast.showSuccess('Invoice emailed successfully');
    } catch (e) {
      toast.showApiError(e, 'Failed to email invoice');
    } finally {
      setEmailing(false);
    }
  };

  const openLinkDialog = async () => {
    if (!invoice) return;
    setLinkOpen(true); setCreatedLink(null);
    try {
      const [a, links, cust] = await Promise.all([
        PaymentLinkService.availability(invoice.institutionId as string),
        PaymentLinkService.forInvoice(invoice.id),
        CustomerService.getById(invoice.customerId).catch(() => null),
      ]);
      setLinkAvail(a); setExistingLinks(links); setLinkEmail(cust?.email);
    } catch (e) {
      toast.showApiError(e, 'Failed to load payment link options');
    }
  };
  const createLink = async (send: boolean) => {
    if (!invoice) return;
    try {
      setLinkBusy(true);
      const l = send ? await PaymentLinkService.createAndSend(invoice.id) : await PaymentLinkService.create(invoice.id);
      setCreatedLink(l);
      setExistingLinks(await PaymentLinkService.forInvoice(invoice.id));
      toast.showSuccess(send ? `Link e-mailed to ${l.sentTo ?? 'the customer'}` : 'Payment link created');
    } catch (e) {
      toast.showApiError(e, 'Failed to create the payment link');
    } finally {
      setLinkBusy(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!invoice) return <Box sx={{ p: 3 }}><Typography>Invoice not found.</Typography></Box>;

  const status: SalesInvoiceStatus = invoice.status;
  const isFc = isForeignCurrency(invoice.currencyCode);
  const balance = invoiceBalanceDue(invoice);

  const isCash = invoice.saleKind === 'CASH';   // FW-2B

  // Status-gated action map
  const can = {
    issue: canManage && status === 'DRAFT',
    editDraft: canManage && status === 'DRAFT',
    delete: canManage && status === 'DRAFT',
    recordReceipt: canManageReceipts && (status === 'ISSUED' || status === 'PARTIALLY_PAID') && !isCash,   // FW-2B: a cash sale is paid at once
    void: canManage && (isCash ? canManageReceipts && (status === 'ISSUED' || status === 'PARTIALLY_PAID' || status === 'PAID')
        : ((status === 'ISSUED' || status === 'PARTIALLY_PAID') && !(invoice.paidAmount > 0))) && !(invoice.creditedAmount && invoice.creditedAmount > 0),
    creditNote: canManage && status !== 'DRAFT' && status !== 'VOIDED' && !invoice.openingBalance,
    // B4-C: an issued foreign-currency invoice is not editable (reverse and re-issue)
    edit: canManage && status === 'ISSUED' && !(invoice.paidAmount > 0) && !isForeignCurrency(invoice.currencyCode),
    pdf: status !== 'DRAFT',
    mushak63: status !== 'DRAFT' && bangladesh && !invoice.openingBalance,
    email: canManage && status !== 'DRAFT',
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.invoice.page">
      <FlowStrip data-help="sales.invoice.flow" stages={salesFlow({ current: 'invoice', quotation: { id: invoice.quotationId, no: invoice.quotationId ? 'Quotation' : null }, invoices: [{ no: invoice.invoiceNumber ?? 'Draft' }], receipts: receipts.map((r, n) => ({ id: r.id, no: `#${n + 1}` })), journalEntryId: invoice.journalEntryId })} />
      {/* Title + action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">
          {invoice.invoiceNumber ?? 'Draft Invoice (unnumbered)'}
          {isCash && <Chip label="Cash sale" color="success" size="small" sx={{ ml: 1 }} />}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {/* ---- FW-3A: Issue waits for the approval when a workflow applies ---- */}
          {can.issue && invoice.approvalRequired && invoice.approvalStatus !== 'APPROVED' && invoice.approvalStatus !== 'PENDING' && (
            <Button data-help="sales.invoice.submitApproval" variant="contained" color="warning" disabled={approvalBusy} onClick={() => handleApproval(true)}>
              {invoice.approvalStatus === 'REJECTED' ? 'Submit for approval again' : 'Submit for approval'}
            </Button>
          )}
          {can.issue && invoice.approvalStatus === 'PENDING' && invoice.approvalCanWithdraw && (
            <Button data-help="sales.invoice.withdrawApproval" variant="outlined" disabled={approvalBusy} onClick={() => handleApproval(false)}>Withdraw approval request</Button>
          )}
          {/* ---- end FW-3A ---- */}
          {can.issue && (
            <Tooltip title={invoice.approvalRequired && invoice.approvalStatus !== 'APPROVED' ? 'This invoice needs an approval before it can be issued' : ''}>
              <span>
                <Button data-help="accounting.invoice.issue" variant="contained" color="primary" onClick={() => setIssueDialogOpen(true)}
                  disabled={!!invoice.approvalRequired && invoice.approvalStatus !== 'APPROVED'}>
                  Issue
                </Button>
              </span>
            </Tooltip>
          )}
          {(can.editDraft || can.edit) && !invoice.openingBalance && (
            <Button data-help="accounting.invoice.edit" variant="outlined" disabled={invoice.approvalStatus === 'PENDING'} onClick={() => navigate(`/sales/invoices/${invoice.id}/edit`)}>
              Edit
            </Button>
          )}
          {can.recordReceipt && (
            <Button data-help="accounting.invoice.recordReceipt" variant="contained" color="success" onClick={() => setReceiptDialogOpen(true)}>
              Record Receipt
            </Button>
          )}
          {can.creditNote && (
            <Button data-help="accounting.invoice.creditNote" variant="outlined" onClick={() => navigate(`/sales/credit-notes/new?invoiceId=${invoice.id}`)}>
              Issue Credit Note
            </Button>
          )}
          {can.pdf && (
            <Button data-help="sales.invoice.pdf" variant="outlined" onClick={handleDownloadPdf} disabled={downloading}>
              {downloading ? <CircularProgress size={20} /> : 'Download PDF'}
            </Button>
          )}
          {can.mushak63 && (
            <Button data-help="sales.invoice.mushak" variant="outlined" onClick={handleDownloadMushak63} disabled={mushakBusy}>
              {mushakBusy ? <CircularProgress size={20} /> : 'Mushak 6.3'}
            </Button>
          )}
          {can.email && canManage && !isFc && !isCash && (status === 'ISSUED' || status === 'PARTIALLY_PAID') && (
            <Button data-help="sales.invoice.paymentLink" variant="outlined" onClick={openLinkDialog}>Payment link</Button>
          )}
          {can.email && (
            <Button data-help="sales.invoice.email" variant="outlined" onClick={handleEmail} disabled={emailing}>
              {emailing ? <CircularProgress size={20} /> : 'Email'}
            </Button>
          )}
          {can.void && (
            <Button data-help="accounting.invoice.void" variant="outlined" color="error" onClick={() => setVoidDialogOpen(true)}>
              {isCash ? 'Void cash sale' : 'Void'}
            </Button>
          )}
          {can.delete && (
            <Button data-help="accounting.invoice.delete" variant="outlined" color="error" onClick={() => setDeleteDialogOpen(true)}>
              Delete
            </Button>
          )}
          {/* ---- FW-2C ---- */}
          {canManage && !isCash && !invoice.openingBalance && invoice.invoiceNumber && (
            <Button data-help="sales.invoice.makeRecurring" variant="outlined" onClick={() => navigate(`/sales/recurring?prefillKind=SALES_INVOICE&prefillDoc=${invoice.id}`)}>Make recurring</Button>
          )}
          {/* ---- end FW-2C ---- */}
          <Button data-help="sales.common.back" variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>
      {/* ---- FW-3A ---- */}
      {(invoice.approvalStatus ?? 'NONE') !== 'NONE' && (
        <ApprovalStatusCard subjectType="SALES_INVOICE" subjectId={invoice.id} refreshKey={`${invoice.approvalStatus}`} />
      )}
      {/* ---- end FW-3A ---- */}

      {/* Header info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Invoice Number</Typography>
            <Typography>{invoice.invoiceNumber ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
              <StatusStagesChip data-help="sales.invoice.statusStages" label={invoiceStatusLabel(invoice, humanizeEnum)} status={status} stages={SALES_INVOICE_STAGES} color={STATUS_COLORS[status]} />
              {invoice.overdue && (
                <Chip label="Overdue" color="error" size="small" />
              )}
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Customer</Typography>
            <Typography>{invoice.customerName ?? invoice.customerId}</Typography>
          </Box>
          {/* ---- P1-A tail: header project (its revenue lines carry the tag) ---- */}
          {invoice.projectId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Project</Typography>
              <Chip data-testid="inv-project-chip" size="small" variant="outlined" sx={{ mt: 0.5 }}
                label={projectTagLabel(invoice.projectCode, invoice.projectName)} />
            </Box>
          )}
          <Box>
            <Typography variant="body2" color="text.secondary">Issue Date</Typography>
            <Typography>{formatDate(invoice.issueDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Due Date</Typography>
            <Typography>{invoice.dueDate ? formatDate(invoice.dueDate) : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography>{formatMoney(invoice.subtotal)}</Typography>
          </Box>
          <DocumentTaxCell total={invoice.taxAmount} components={taxComponents} />
          <Box>
            <Typography variant="body2" color="text.secondary">Total Amount</Typography>
            <Typography fontWeight="bold">{formatMoney(invoice.totalAmount)}</Typography>
          </Box>
          <FcAmountLine currency={invoice.currencyCode} rate={invoice.exchangeRate} foreign={invoice.foreignTotal}
            bdt={invoice.totalAmount} foreignOutstanding={invoice.foreignOutstanding} />
          <Box>
            <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
            <Typography>{formatMoney(invoice.paidAmount)}</Typography>
          </Box>
          {(invoice.creditedAmount ?? 0) > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary">Credited</Typography>
              <Typography>{formatMoney(invoice.creditedAmount)}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="body2" color="text.secondary">Balance Due</Typography>
            <Typography fontWeight="bold" color={balance > 0 ? 'error.main' : 'text.primary'}>
              {formatMoney(balance)}
              {status === 'VOIDED' && <Chip label="Voided" color="default" size="small" sx={{ ml: 1 }} />}
            </Typography>
          </Box>
          {invoice.notes && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">Notes</Typography>
              <Typography>{invoice.notes}</Typography>
            </Box>
          )}
          {invoice.journalEntryId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Journal Entry</Typography>
              <Link
                component={RouterLink}
                to={`/accounting/journal-entries/${invoice.journalEntryId}`}
              >
                View JE
              </Link>
            </Box>
          )}
          {invoice.reversalJournalEntryId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Reversal Journal Entry</Typography>
              <Link
                component={RouterLink}
                to={`/accounting/journal-entries/${invoice.reversalJournalEntryId}`}
              >
                View Reversal JE
              </Link>
            </Box>
          )}
          {status === 'VOIDED' && invoice.voidReason && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">Void Reason</Typography>
              <Typography color="error.main">{invoice.voidReason}</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Line items table */}
      {invoice.lines.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }} data-help-section="sales.invoice.lines">
          <Typography variant="h6" sx={{ mb: 2 }}>Line Items</Typography>
          <ScrollableTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="sales.invoice.lines.col.description">Description</TableCell>
                    <TableCell align="right" data-help="sales.invoice.lines.col.quantity">Quantity</TableCell>
                    <TableCell align="right" data-help="sales.invoice.lines.col.unitPrice">{isFc ? `Unit Price (${invoice.currencyCode})` : 'Unit Price'}</TableCell>
                    {anyDisc && <TableCell align="right" data-help="sales.invoice.lines.col.gross">{isFc ? `Gross (${invoice.currencyCode})` : 'Gross'}</TableCell>}   {/* FW-2A */}
                    {anyDisc && <TableCell align="right" data-help="sales.invoice.lines.col.discount">{isFc ? `Discount (${invoice.currencyCode})` : 'Discount'}</TableCell>}
                    {anyCodedLine && <TableCell data-help="sales.invoice.lines.col.taxCode">Tax code</TableCell>}
                    <TableCell align="right" data-help="sales.invoice.lines.col.taxPct">{anyCodedLine ? 'Tax % (combined)' : 'Tax %'}</TableCell>
                    <TableCell align="right" data-help="sales.invoice.lines.col.lineTax">Line Tax</TableCell>
                    <TableCell align="right" data-help="sales.invoice.lines.col.lineTotal">{isFc ? `Line Total (${invoice.currencyCode})` : 'Line Total'}</TableCell>
                    {isFc && <TableCell align="right" data-help="sales.invoice.lines.col.lineTotalBdt">Line Total (BDT)</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.lines.map(line => (
                    <TableRow key={line.id}>
                      <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{line.description}</TableCell>
                      <TableCell align="right">{line.quantity}</TableCell>
                      <TableCell align="right">{formatUnitPrice(isFc && line.foreignUnitPrice != null ? line.foreignUnitPrice : line.unitPrice)}</TableCell>
                      {anyDisc && <TableCell align="right">{formatMoney(isFc && line.foreignAmount != null ? line.foreignAmount + Number(line.foreignDiscountAmount ?? 0) : (line.grossAmount ?? line.lineTotal))}</TableCell>}   {/* FW-2A: foreign invoices show the document currency */}
                      {anyDisc && <TableCell align="right">{(() => { const d = Number((isFc ? line.foreignDiscountAmount : line.discountAmount) ?? 0); return d > 0 ? `${formatMoney(d)}${line.discountPct != null ? ` (${line.discountPct}%)` : ''}` : '-'; })()}</TableCell>}
                      {anyCodedLine && <TableCell>{taxCodeLabel(taxLookup, line.taxCodeId)}</TableCell>}
                      <TableCell align="right">{line.taxRatePct}%</TableCell>
                      <TableCell align="right">{formatMoney(line.lineTax)}</TableCell>
                      <TableCell align="right">
                        {isFc && line.foreignAmount != null ? formatMoney(line.foreignAmount) : formatMoney(line.lineTotal)}
                      </TableCell>
                      {isFc && <TableCell align="right">{formatMoney(line.lineTotal)}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          </ScrollableTable>
        </Paper>
      )}

      {/* Receipt history */}
      <Paper sx={{ p: 3, mb: 3 }} data-help-section="sales.invoice.receipts">
        <Typography variant="h6" sx={{ mb: 2 }}>Receipt History</Typography>
        {receipts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No receipts recorded.</Typography>
        ) : (
          <ScrollableTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="sales.invoice.receipts.col.number">Receipt #</TableCell>
                    <TableCell data-help="sales.invoice.receipts.col.date">Date</TableCell>
                    <TableCell align="right" data-help="sales.invoice.receipts.col.amount">Amount</TableCell>
                    {isFc && <TableCell align="right" data-help="sales.invoice.receipts.col.carrying">Carrying (BDT)</TableCell>}
                    {isFc && <TableCell align="right" data-help="sales.invoice.receipts.col.fx">Realised FX</TableCell>}
                    <TableCell data-help="sales.invoice.receipts.col.method">Method</TableCell>
                    <TableCell data-help="sales.invoice.receipts.col.status">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receipts.map(r => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <Link component={RouterLink} to={`/sales/receipts/${r.id}`}>
                          {r.receiptNumber ?? r.id}
                        </Link>
                      </TableCell>
                      <TableCell>{formatDate(r.receiptDate)}</TableCell>
                      <TableCell align="right">
                        {isFc && r.foreignAmount != null
                          ? <>{formatFc(r.currencyCode ?? invoice.currencyCode!, r.foreignAmount)}
                              {r.exchangeRate != null && <Typography variant="caption" color="text.secondary" display="block">at {r.exchangeRate}</Typography>}</>
                          : formatMoney(r.appliedAmount ?? r.amount)}
                      </TableCell>
                      {isFc && <TableCell align="right">{formatMoney(r.appliedAmount ?? r.amount)}</TableCell>}
                      {isFc && <TableCell align="right">{r.fxDifference != null ? formatMoney(r.fxDifference) : '-'}</TableCell>}
                      <TableCell>{humanizeEnum(r.method)}</TableCell>
                      <TableCell>
                        {r.status === 'REVERSED' ? (
                          <Chip label="Reversed" color="error" size="small" />
                        ) : (
                          <Chip label="Posted" color="success" size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          </ScrollableTable>
        )}
      </Paper>

      {creditNotes.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }} data-help-section="sales.invoice.creditNotes">
          <Typography variant="h6" sx={{ mb: 2 }}>Credit Notes</Typography>
          <ScrollableTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="sales.invoice.creditNotes.col.number">Credit note #</TableCell>
                  <TableCell data-help="sales.invoice.creditNotes.col.date">Date</TableCell>
                  <TableCell align="right" data-help="sales.invoice.creditNotes.col.total">Total</TableCell>
                  <TableCell data-help="sales.invoice.creditNotes.col.status">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {creditNotes.map(n => (
                  <TableRow key={n.id}>
                    <TableCell>
                      <Link component={RouterLink} to={`/sales/credit-notes/${n.id}`}>{n.creditNoteNumber}</Link>
                    </TableCell>
                    <TableCell>{formatDate(n.noteDate)}</TableCell>
                    <TableCell align="right">{formatMoney(n.totalAmount)}</TableCell>
                    <TableCell>
                      <Chip label={humanizeEnum(n.status)} color={n.status === 'POSTED' ? 'success' : 'default'} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTable>
        </Paper>
      )}

      {/* Issue confirm dialog */}
      <Dialog open={issueDialogOpen} onClose={() => setIssueDialogOpen(false)} data-help-page="sales.invoice.issueDialog.page">
        <DialogTitle>Issue this invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Issuing the invoice will post a journal entry and assign an invoice number.
            The invoice can no longer be deleted after issuance.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.invoice.issueDialog.cancel" onClick={() => setIssueDialogOpen(false)} disabled={issuing}>Cancel</Button>
          <Button data-help="accounting.invoice.issue" variant="contained" color="primary" onClick={handleIssue} disabled={issuing}>
            {issuing ? <CircularProgress size={20} /> : 'Confirm Issue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} data-help-page="sales.invoice.deleteDialog.page">
        <DialogTitle>Delete this draft invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the draft invoice. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.invoice.deleteDialog.cancel" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button data-help="accounting.invoice.delete" variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void dialog — collects voidReason (ReverseConfirmDialog has no reason field) */}
      <Dialog open={voidDialogOpen} onClose={() => setVoidDialogOpen(false)} fullWidth maxWidth="sm" data-help-page="sales.invoice.voidDialog.page">
        <DialogTitle>{isCash ? 'Void this cash sale?' : 'Void this invoice?'}</DialogTitle>
        <DialogContent data-help-section="sales.invoice.voidDialog.section.reason">
          <DialogContentText sx={{ mb: 2 }}>
            Voiding the invoice will post a reversal journal entry. This cannot be undone.
            Any outstanding balance will be cleared. A void reason is required.
          </DialogContentText>
          <TextField data-help="sales.invoice.voidDialog.reason"
            label="Void Reason"
            value={voidReason}
            onChange={e => setVoidReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.invoice.voidDialog.cancel" onClick={() => { setVoidDialogOpen(false); setVoidReason(''); }} disabled={voiding}>
            Cancel
          </Button>
          <Button
            data-help="accounting.invoice.void"
            variant="contained"
            color="error"
            onClick={handleVoid}
            disabled={voiding || voidReason.trim().length === 0}
          >
            {voiding ? <CircularProgress size={20} /> : 'Confirm Void'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Receipt dialog */}
      {receiptDialogOpen && (
        <RecordReceiptDialog
          open={receiptDialogOpen}
          invoice={invoice}
          onClose={() => setReceiptDialogOpen(false)}
          onPosted={async () => {
            setReceiptDialogOpen(false);
            await reload();
          }}
        />
      )}

      {linkOpen && (
        <Dialog open onClose={() => setLinkOpen(false)} maxWidth="sm" fullWidth data-help-page="sales.invoice.linkDialog.page">
          <DialogTitle>Online payment link</DialogTitle>
          <DialogContent>
            {!linkAvail && <CircularProgress size={20} />}
            {linkAvail && !linkAvail.anyEnabled && (
              <Alert severity="info">No online payment gateway is enabled. Set one up under Sales &gt; Online Payments Settings.</Alert>
            )}
            {linkAvail?.anyEnabled && !createdLink && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  A link lets {invoice.customerName ?? 'the customer'} pay the outstanding amount of this invoice online
                  ({linkAvail.providers.map(p => p.label).join(', ')}).
                </Typography>
                {linkAvail.providers.some(p => p.mode === 'SANDBOX') && <Alert severity="warning" sx={{ mb: 1 }}>Sandbox mode: no real money moves.</Alert>}
                {existingLinks.some(l => l.status === 'OPEN') && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    This invoice already has a live link. A link can only be shown once, so cancel it under Sales &gt; Payment Links to issue a new one.
                  </Alert>
                )}
                <Typography variant="body2">E-mail would be sent to: <strong>{linkEmail ?? 'no e-mail on the customer record'}</strong></Typography>
              </>
            )}
            {createdLink?.url && (
              <>
                <Alert severity="success" sx={{ mb: 1 }}>Link created. Copy it now: it will not be shown again.</Alert>
                <TextField data-help="sales.invoice.dlg.linkUrl" fullWidth size="small" value={createdLink.url} InputProps={{ readOnly: true }} />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button data-help="sales.invoice.dlg.close" onClick={() => setLinkOpen(false)}>Close</Button>
            {linkAvail?.anyEnabled && !createdLink && !existingLinks.some(l => l.status === 'OPEN') && (
              <>
                <Button data-help="sales.invoice.dlg.createLink" disabled={linkBusy} onClick={() => createLink(false)}>Create link only</Button>
                <Button data-help="sales.invoice.dlg.createLinkEmail" variant="contained" disabled={linkBusy || !linkEmail} onClick={() => createLink(true)}>Create and e-mail</Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}

      <AttachmentsPanel entityType="SALES_INVOICE" entityId={invoice.id} canManage={canManage} />
    </Box>
  );
};

export default SalesInvoiceDetail;
